import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { callsAPI } from '../../services/api';
import { formatDateTimeInViewerTZ } from '../../utils/dateTimeUtils';
import { getCallTabId } from './callTab';
import {
  askResultCopy,
  defaultAskReason,
  dialRefusal,
  offerFromPermission,
} from './callOffer';

function errorBody(err) {
  return err?.response?.data || err?.data || {};
}

export function latestMissedCall(items) {
  return (items || []).find((item) => /missed whatsapp call/i.test(item?.summary || '')) || null;
}

export default function useThreadCall({
  contactId,
  bpContactId,
  leadId,
  parentName,
  childName,
  agentName,
  timezone,
}) {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState('');
  const [tone, setTone] = useState('info');
  const [busy, setBusy] = useState(false);
  const [patch, setPatch] = useState(null);
  const [reason, setReason] = useState('');
  const editedReason = useRef(false);
  const suggested = defaultAskReason({ agent: agentName, child: childName || parentName });

  const status = useQuery(['calling-status'], () => callsAPI.status(), {
    staleTime: 60000,
    retry: false,
  });
  const enabled = Boolean(status.data?.data?.enabled);
  const log = useQuery(
    ['calling-log', contactId],
    () => callsAPI.log(contactId),
    { enabled: Boolean(enabled && contactId), retry: false }
  );
  const permissionQuery = useQuery(
    ['call-permission', bpContactId],
    () => callsAPI.permission(bpContactId),
    { enabled: Boolean(enabled && bpContactId), retry: false, staleTime: 15000 }
  );

  useEffect(() => {
    editedReason.current = false;
    setPatch(null);
    setMessage('');
  }, [bpContactId]);

  useEffect(() => {
    if (!editedReason.current) setReason(suggested);
  }, [bpContactId, suggested]);

  useEffect(() => {
    const onPermission = (event) => {
      const detail = event.detail || {};
      if (!bpContactId || String(detail.bpContactId) !== String(bpContactId)) return;
      setPatch(null);
      queryClient.invalidateQueries(['call-permission', bpContactId]);
    };
    window.addEventListener('bp-call-permission', onPermission);
    return () => window.removeEventListener('bp-call-permission', onPermission);
  }, [bpContactId, queryClient]);

  const items = log.data?.data?.items || [];
  const permission = { ...(permissionQuery.data?.data || {}), ...(patch || {}) };
  const offer = permissionQuery.isSuccess || patch
    ? offerFromPermission(permission)
    : { mode: 'loading' };

  function note(text, kind = 'info') {
    setTone(kind);
    setMessage(text);
  }

  function applyPatch(next) {
    if (!next) return;
    setPatch((current) => ({ ...(current || {}), ...next }));
  }

  function editReason(value) {
    editedReason.current = true;
    setReason(value);
  }

  async function start() {
    if (busy || offer.mode !== 'call') return;
    setBusy(true);
    note('');
    window.dispatchEvent(new CustomEvent('bp-call-prime'));
    try {
      const res = await callsAPI.start({
        bpContactId,
        leadId: leadId || null,
        tapId: `${Date.now()}`,
        tabId: getCallTabId(),
      });
      if (res.status === 201 && res.data?.voice) {
        window.dispatchEvent(new CustomEvent('bp-call-voice', {
          detail: {
            voice: res.data.voice,
            callId: res.data.call_id,
            name: parentName || childName || 'Parent',
            outbound: true,
            contactId,
          },
        }));
      } else if (res.status === 201) {
        note('The call started, but this browser did not receive audio.', 'error');
      } else {
        const refusal = dialRefusal(res.data);
        applyPatch(refusal.patch);
        if (refusal.code !== 'DO_NOT_CALL') note(refusal.message, 'error');
      }
    } catch (err) {
      const refusal = dialRefusal(errorBody(err));
      applyPatch(refusal.patch);
      if (refusal.code !== 'DO_NOT_CALL') note(refusal.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function ask() {
    const text = reason.replace(/\s+/g, ' ').trim();
    if (busy || !text || offer.mode !== 'ask') return;
    setBusy(true);
    note('');
    try {
      const res = await callsAPI.requestPermission({ bpContactId, bodyText: text });
      note(askResultCopy(res.data));
      queryClient.invalidateQueries(['call-permission', bpContactId]);
    } catch (err) {
      const refusal = dialRefusal(errorBody(err));
      applyPatch(refusal.patch);
      if (refusal.code !== 'DO_NOT_CALL') note(refusal.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  async function setDoNotCall(value) {
    if (busy) return;
    setBusy(true);
    note('');
    try {
      await callsAPI.setDoNotCall({
        bpContactId,
        value,
        reason: value ? 'parent asked' : 'cleared',
      });
      applyPatch({ do_not_call: value });
      note(value ? 'Calls are turned off for this parent.' : 'Calls are allowed again.');
      queryClient.invalidateQueries(['call-permission', bpContactId]);
    } catch (err) {
      const refusal = dialRefusal(errorBody(err));
      note(refusal.message, 'error');
    } finally {
      setBusy(false);
    }
  }

  const waitWhen = offer.mode === 'wait' && offer.nextRequestAt
    ? formatDateTimeInViewerTZ(offer.nextRequestAt, timezone)
    : '';
  const waitText = offer.mode === 'wait'
    ? (waitWhen ? `You can ask again after ${waitWhen}.` : 'You cannot ask to call right now.')
    : '';

  return {
    enabled,
    bpContactId,
    offer,
    reason,
    editReason,
    busy,
    message,
    tone,
    waitText,
    items,
    missed: latestMissedCall(items),
    start,
    ask,
    setDoNotCall,
    permissionLoading: Boolean(permissionQuery.isLoading),
    permissionError: Boolean(permissionQuery.isError && !patch),
  };
}
